import LinkNoPrefetch from '@/components/link-noprefetch';

import { decode } from 'html-entities';

import { DotCMSBlockEditorRenderer } from '@dotcms/react';
import { isInternalURL } from '@/components/utils/urlUtils';
import { useEffect } from 'react';
import { transformFAQs } from '@/components/block-editor/faq';

/**
 * Renders the text in bold.
 *
 * @param children - The content to be rendered in bold.
 */
const Bold = ({ children }) => <strong>{children}</strong>;

/**
 * Renders the text in italic format.
 *
 * @param children - The content to be rendered in italic.
 */
const Italic = ({ children }) => <em>{children}</em>;

/**
 * Renders a strike-through text.
 *
 * @param children - The content to be rendered within the strike-through element.
 */
const Strike = ({ children }) => <s>{children}</s>;

/**
 * Renders an underline element for the given children.
 *
 * @param children - The content to be underlined.
 */
const Underline = ({ children }) => <u>{children}</u>;

/**
 * Renders a link component.
 *
 * @param children - The content of the link.
 * @param attrs - The attributes to be applied to the link.
 * @returns The rendered link component.
 */
const enforceHttps = (href) => {
    if (typeof href === 'string' && href.startsWith('http://')) {
        return 'https://' + href.slice(7);
    }
    return href;
};

const LinkMark = ({ children, attrs }) => {
    const { href: rawHref, rel, target, ...restAttrs } = attrs;
    const href = enforceHttps(rawHref);
    const isInternal = isInternalURL(href);
    let isDevDotcms = false;

    if (href) {
        try {
            const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : '');
            isDevDotcms = url.hostname === 'dev.dotcms.com';
        } catch {}
    }

    if (isInternal) {
        const finalAttrs = {
            ...restAttrs,
            href: href || '/',
            ...(isDevDotcms && { target: '_blank' })
        };

        return <LinkNoPrefetch {...finalAttrs}>{children}</LinkNoPrefetch>;
    }

    const finalAttrs = {
        ...restAttrs,
        href: href || '/',
        ...(target && { target }),
        ...(rel && { rel }),
        ...(isDevDotcms && { target: '_blank' })
    };

    return <a {...finalAttrs}>{children}</a>;
};

/**
 * Renders the superscript text.
 *
 * @param children - The content to be rendered as superscript.
 */
const Superscript = ({ children }) => <sup>{children}</sup>;

/**
 * Renders a subscript element.
 *
 * @param children - The content to be rendered as subscript.
 */
const Subscript = ({ children }) => <sub>{children}</sub>;

const nodeMarks = {
    link: LinkMark,
    bold: Bold,
    underline: Underline,
    italic: Italic,
    strike: Strike,
    superscript: Superscript,
    subscript: Subscript
};

/**
 * Renders a text block with optional marks.
 *
 * @param props - The props for the TextBlock component.
 * @returns The rendered text block.
 */
const TextBlock = (props) => {
    const { marks = [], text } = props.node;
    const mark = marks && marks.length > 0 ? marks[0] : { type: '', attrs: {} };
    const newProps = { ...props, node: { ...props.node, marks: marks.slice(1) } };
    const Component = nodeMarks[mark?.type];
    // To avoid the warning: "Warning: Invalid DOM property `class`. Did you mean `className`?"
    if (mark.attrs) {
        mark.attrs.className = mark.attrs.class;
        delete mark.attrs.class;
    }

    // Decode HTML entities like &nbsp;, &amp;, &lt;, &gt;, &quot;, etc.
    const decodedText = text ? decode(text) : text;

    if (!Component) {
        return decodedText;
    }

    return (
        <Component attrs={mark.attrs}>
            <TextBlock {...newProps} />
        </Component>
    );
};

export const DecodeHTML = (props) => {
    const {
        node: { text }
    } = props;
    return <TextBlock {...props} text={decode(text)} />;
};

export const DotBlockEditor = ({ customRenderers, ...props }) => {
    const hasBlocks = props.blocks?.json;
    const hasContent = props.blocks?.content;
    useEffect(() => {
        if (hasBlocks === undefined && hasContent === undefined) {
            console.warn(
                '[WARNING]: GraphQL block has no json property or body property, contact dotCMS team to fix this issue'
            );
        }
    }, [hasBlocks, hasContent]);
    // NOTE: Now that we are using GraphQL, we need to check if the blocks have a json property
    // TODO: Discuss with the team if the `json` property will be a convention and will be always present.
    if (hasBlocks === undefined && hasContent === undefined) {
        return null;
    }

    const blocks = transformFAQs(props.blocks?.json ?? props.blocks);
    return (
        <DotCMSBlockEditorRenderer
            {...props}
            blocks={blocks}
            customRenderers={{
                text: DecodeHTML,
                ...customRenderers
            }}
        />
    );
};
